import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { dialogAnatomy } from '@chakra-ui/react/anatomy';

/**
 * Het Chakra-systeem van AME, overgezet uit `client/imports/ui/Theme.ts` van het origineel
 * (Chakra v1 met `extendTheme`; uitgepakt in `AME Old/naslag/client/modules`).
 *
 * De kleurnamen zijn gelijk gebleven, zodat `colorScheme="magazijn"` uit het origineel hier
 * `colorPalette="magazijn"` wordt. Elk palet heeft de stappen 50 tot en met 900 en de
 * meeste ook `l`, `m` en `d` (licht, midden, donker), die de schermen rechtstreeks
 * gebruiken (`magazijn.l` als achtergrond, `obs.d` als tekstkleur). Chakra v3 kleurt zijn
 * componenten met semantische tokens (`colorPalette.solid`, `.fg`, `.muted` enzovoort);
 * die staan per palet in `semanticTokens`, anders zou een `Button colorPalette="obs"` geen
 * kleur hebben.
 *
 * De varianten uit het origineel zijn uitbreidingen op de standaardrecepten van Chakra:
 * - `Button`: standaard `colorPalette="ameBlue"` en `size="lg"`;
 * - `Dialog` (was `Modal`): `variant="popup" | "darkPopup"`.
 * De knoppen van het fabrieksmenu (`menu`), de knop "Meer informatie" (`roleScreenInfo`), de
 * stakeholderpopup (`stakeholder`) en de archieftabel (`archief`) hadden in het origineel een
 * eigen variant; hun nieuwe ontwerp staat in de schermen zelf.
 *
 * Draai na een wijziging aan tokens of recepten de typegen, zodat TypeScript ze kent:
 * `npm run typegen -w client` (of `npx @chakra-ui/cli typegen src/theme/system.ts` in client/).
 */

/** De standaardachtergrond van het spel; schermen zetten hun eigen kleur via `--background-color`. */
const ACHTERGROND = '#c4e3e8';

/** Zo noemt v1 zijn standaardletter voor koppen; het origineel veranderde alleen `body`. */
const SYSTEEMLETTER = `-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;

type Stappen = Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
type Palet = Stappen & Partial<Record<'l' | 'm' | 'd', string>>;

/** De kleuren uit het origineel, letterlijk overgenomen. */
const paletten = {
  onderhoud: {
    50: '#FBF8E9',
    100: '#F5EBC1',
    200: '#EFDF9A',
    300: '#E9D272',
    400: '#E2C54B',
    500: '#DCB923',
    600: '#B0941C',
    700: '#846F15',
    800: '#584A0E',
    900: '#2C2507',
    l: '#f2e5ae',
    m: '#e5cb5c',
    d: '#654b00',
  },
  storing: {
    50: '#FDF9E8',
    100: '#F9EFBE',
    200: '#F5E494',
    300: '#F1DA6A',
    400: '#EDD040',
    500: '#E9C516',
    600: '#BB9E11',
    700: '#8C760D',
    800: '#5D4F09',
    900: '#2F2704',
    l: '#e7d789',
    m: '#ceae13',
    d: '#4f2f00',
  },
  veiligheid: {
    50: '#FBEBEA',
    100: '#F3C7C3',
    200: '#ECA39D',
    300: '#E47F76',
    400: '#DD5A50',
    500: '#D5362A',
    600: '#AB2B21',
    700: '#802119',
    800: '#551611',
    900: '#2B0B08',
    l: '#f4c8c4',
    m: '#e89089',
    d: '#691109',
  },
  incident: {
    50: '#FBEAE9',
    100: '#F5C4C2',
    200: '#EE9E9B',
    300: '#E87973',
    400: '#E1534C',
    500: '#DA2D25',
    600: '#AF241D',
    700: '#831B16',
    800: '#57120F',
    900: '#2C0907',
    l: '#f0a6a3',
    m: '#e04d46',
    d: '#610000',
  },
  revisie: {
    50: '#FBEAF4',
    100: '#F4C3DF',
    200: '#EC9CCA',
    300: '#E576B6',
    400: '#DE4FA1',
    500: '#D6298D',
    600: '#AC2070',
    700: '#811854',
    800: '#561038',
    900: '#2B081C',
    l: '#eea5cf',
    m: '#dd4b9f',
    d: '#5d001f',
  },
  productie: {
    50: '#E8F5FC',
    100: '#C0E3F7',
    200: '#97D1F1',
    300: '#6FBFEC',
    400: '#46ADE7',
    500: '#1D9BE2',
    600: '#177CB5',
    700: '#125D87',
    800: '#0C3E5A',
    900: '#061F2D',
    l: '#abdaf4',
    m: '#56b4e9',
    d: '#003569',
  },
  organisatie: {
    50: '#E9EAFB',
    100: '#C1C4F5',
    200: '#9A9EEF',
    300: '#7278E9',
    400: '#4B52E2',
    500: '#232CDC',
    600: '#1C23B0',
    700: '#151B84',
    800: '#0E1258',
    900: '#07092C',
    l: '#9ea2f0',
    m: '#3d45e0',
    d: '#000061',
  },
  magazijn: {
    50: '#F2FAEB',
    100: '#DBF0C7',
    200: '#C4E6A3',
    300: '#ADDC7E',
    400: '#96D35A',
    500: '#7EC936',
    600: '#65A12B',
    700: '#4C7821',
    800: '#335016',
    900: '#19280B',
    l: '#c3e6a1',
    m: '#86cc42',
    d: '#074d00',
  },
  assetData: {
    50: '#F2EAFB',
    100: '#DCC4F3',
    200: '#C59EEB',
    300: '#AF78E3',
    400: '#9852DB',
    500: '#822CD3',
    600: '#6823A9',
    700: '#4E1A7F',
    800: '#341254',
    900: '#1A092A',
    l: '#cca9ed',
    m: '#9953db',
    d: '#19005b',
  },
  obs: {
    50: '#E5FFF8',
    100: '#B8FFEC',
    200: '#8AFFDF',
    300: '#5CFFD3',
    400: '#2EFFC6',
    500: '#00FFBA',
    600: '#00CC94',
    700: '#00996F',
    800: '#00664A',
    900: '#003325',
    l: '#80cfb9',
    m: '#009e73',
    d: '#001f00',
  },
  archief: {
    50: '#F4F3F0',
    100: '#E1DDD5',
    200: '#CEC8BB',
    300: '#BBB2A0',
    400: '#A89C85',
    500: '#95876A',
    600: '#776C55',
    700: '#595140',
    800: '#3B362B',
    900: '#1E1B15',
    l: '#c9beb1',
    m: '#897c62',
    d: '#332718',
  },
  ameGrey: {
    50: '#F0F2F4',
    100: '#D5DBE1',
    200: '#BBC4CE',
    300: '#A0ACBB',
    400: '#8595A8',
    500: '#6A7E95',
    600: '#556577',
    700: '#404C59',
    800: '#2A323C',
    900: '#15191E',
  },
  ameBlue: {
    50: '#E5F0FF',
    100: '#B8D5FF',
    200: '#8ABAFF',
    300: '#5C9FFF',
    400: '#2E83FF',
    500: '#0068FF',
    600: '#0053CC',
    700: '#003F99',
    800: '#002A66',
    900: '#001533',
    l: '#5C9FFF',
    m: '#0068FF',
    d: '#002A66',
  },
} satisfies Record<string, Palet>;

type PaletNaam = keyof typeof paletten;

/** `{ 50: '#..' }` wordt `{ 50: { value: '#..' } }`, de vorm van een token in v3. */
function alsTokens(palet: Palet) {
  return Object.fromEntries(
    Object.entries(palet).map(([stap, kleur]) => [stap, { value: kleur }]),
  ) as Record<keyof Palet, { value: string }>;
}

/**
 * De semantische tokens waarmee de recepten van Chakra v3 een palet inkleuren, naar het
 * model van de ingebouwde paletten. `solid` is stap 500 en de tekst erop wit, zoals de
 * knoppen in v1 waren. Het donkere thema staat er voor de volledigheid bij; de provider
 * dwingt licht af.
 */
function semantischPalet(naam: PaletNaam) {
  const stap = (n: number) => `{colors.${naam}.${n}}`;
  return {
    solid: { value: stap(500) },
    contrast: { value: 'white' },
    fg: { value: { _light: stap(700), _dark: stap(300) } },
    muted: { value: { _light: stap(200), _dark: stap(800) } },
    subtle: { value: { _light: stap(100), _dark: stap(900) } },
    emphasized: { value: { _light: stap(300), _dark: stap(700) } },
    focusRing: { value: stap(500) },
    border: { value: { _light: stap(500), _dark: stap(400) } },
  };
}

const paletNamen = Object.keys(paletten) as PaletNaam[];

/**
 * Twee varianten van de popups uit het origineel (`popup` en `darkPopup`).
 * De kleur komt uit `colorPalette`; zet die op `Dialog.Positioner` of `Dialog.Content`,
 * want `Dialog.Root` rendert zelf geen element.
 */
function popup({ donker = false }) {
  const kleur = (stap: number) => (donker ? 'colorPalette.d' : `colorPalette.${stap}`);
  return {
    positioner: { alignItems: 'center' },
    content: {
      borderRadius: '0',
      textStyle: '2xl',
      border: '5px solid',
      borderColor: 'currentColor',
      color: kleur(500),
    },
    backdrop: { bg: 'whiteAlpha.600' },
    header: { textStyle: '3xl' },
    title: { textStyle: '3xl' },
    // Bedoeld voor `<Dialog.CloseTrigger aria-label="Sluiten"><X /></Dialog.CloseTrigger>`
    // zonder `CloseButton` erin: die heeft een eigen recept dat met dit zou botsen.
    closeTrigger: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      top: '3',
      insetEnd: '3',
      p: '2',
      textStyle: '2xl',
      bg: kleur(500),
      color: 'white',
      borderRadius: 'none',
      cursor: 'pointer',
      _hover: { bg: kleur(600) },
      _active: { bg: kleur(700) },
      _icon: { boxSize: '1em' },
    },
  };
}

const config = defineConfig({
  globalCss: {
    // Elk scherm zet zijn eigen achtergrond met `useAchtergrondkleur`; de overgang maakt
    // de wissel tussen schermen zacht, zoals in het origineel.
    ':root': { '--background-color': ACHTERGROND },
    'html, body': {
      background: 'var(--background-color) !important',
      boxSizing: 'border-box',
      margin: '0',
      padding: '0',
      overflowX: 'hidden',
      overflowY: 'hidden',
      width: '100vw',
      transitionProperty: 'background-color',
      transitionDuration: 'normal',
    },
    // Invoervelden liggen op gekleurde achtergronden en moesten wit blijven.
    input: { backgroundColor: 'white !important' },
    // De klassen uit Utils/Blink.css en Utils/Breathe.css. Ze blijven als klasse bestaan,
    // zodat `className="blink"` uit het origineel ongewijzigd werkt. Wie minder beweging
    // wil, krijgt een vaste rode rand en een stilstaand logo.
    '.blink': {
      animation: 'blink 0.2s infinite alternate',
      _motionReduce: { animation: 'none', borderColor: 'red' },
    },
    '.breathe': {
      animation: 'breathe 0.8s ease-in-out infinite alternate',
      _motionReduce: { animation: 'none' },
    },
  },
  theme: {
    tokens: {
      colors: {
        ...Object.fromEntries(paletNamen.map((naam) => [naam, alsTokens(paletten[naam])])),
        ameBackground: { value: ACHTERGROND },
      },
      fonts: {
        // Cabin wordt geladen in index.html.
        body: { value: `Cabin, ${SYSTEEMLETTER}` },
        heading: { value: SYSTEEMLETTER },
      },
    },
    semanticTokens: {
      colors: Object.fromEntries(paletNamen.map((naam) => [naam, semantischPalet(naam)])),
    },
    keyframes: {
      blink: {
        '0%': { borderColor: 'red' },
        '100%': { borderColor: 'orange' },
      },
      breathe: {
        '0%': { transform: 'scale(1)' },
        '100%': { transform: 'scale(1.15)' },
      },
      // Niet in het origineel: de fabriek op het welkomstscherm zweeft en rookt.
      zweven: {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-12px)' },
      },
      rook: {
        '0%': { transform: 'translate(0, 0) scale(0.4)', opacity: '0' },
        '15%': { opacity: '0.85' },
        '100%': { transform: 'translate(-30px, -80px) scale(1.7)', opacity: '0' },
      },
    },
    recipes: {
      button: {
        // In v1 stond dit in `defaultProps`. Een `colorPalette`-prop gaat er nog steeds boven.
        base: { colorPalette: 'ameBlue' },
        defaultVariants: { size: 'lg' },
      },
    },
    slotRecipes: {
      // `slots` is verplicht in het type; het zijn dezelfde als in het standaardrecept.
      dialog: {
        slots: dialogAnatomy.keys(),
        variants: {
          variant: {
            popup: popup({}),
            darkPopup: popup({ donker: true }),
          },
        },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
